/**
 * Tabler-backed icon registry — the default icon set for @platform-blocks/ui.
 *
 * Every icon is a component from `@tabler/icons-react-native`. `<Icon name="..." />`
 * resolves these by key; the Icon component picks `filled`/`outlined` based on the
 * `variant` prop (falling back to outlined when no filled glyph exists).
 *
 * To add an icon: import it from `@tabler/icons-react-native` above and add a
 * `name: { outlined: IconX, filled?: IconXFilled }` entry below.
 */
import {
  IconAdjustments,
  IconAdjustmentsFilled,
  IconAdjustmentsHorizontal,
  IconAlertCircle,
  IconAlertCircleFilled,
  IconAlertTriangle,
  IconAlertTriangleFilled,
  IconAlignCenter,
  IconAlignJustified,
  IconAlignLeft,
  IconAlignRight,
  IconAppWindow,
  IconArrowBackUp,
  IconArrowDown,
  IconArrowForwardUp,
  IconArrowLeft,
  IconArrowRight,
  IconArrowUp,
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconArrowsSort,
  IconBadge,
  IconBan,
  IconBell,
  IconBellFilled,
  IconBinaryTree,
  IconBluetooth,
  IconBold,
  IconBolt,
  IconBoltFilled,
  IconBone,
  IconBookmark,
  IconBookmarkFilled,
  IconBorderOuter,
  IconBox,
  IconBuildingStore,
  IconCalendar,
  IconCalendarFilled,
  IconCamera,
  IconCameraFilled,
  IconCards,
  IconCarouselHorizontal,
  IconChartArea,
  IconChartBar,
  IconChartDonut,
  IconChartDots,
  IconChartLine,
  IconChartPie,
  IconCheck,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconChevronUp,
  IconCircle,
  IconCircleCheck,
  IconCircleCheckFilled,
  IconCircleDot,
  IconCircleFilled,
  IconClipboardList,
  IconClock,
  IconClockFilled,
  IconCode,
  IconColorSwatch,
  IconColumns,
  IconContrast,
  IconContrastFilled,
  IconCopy,
  IconCursorText,
  IconDatabase,
  IconDeviceFloppy,
  IconDots,
  IconDownload,
  IconExclamationCircle,
  IconExclamationCircleFilled,
  IconExternalLink,
  IconEye,
  IconEyeFilled,
  IconEyeOff,
  IconFile,
  IconFileDescription,
  IconFileFilled,
  IconFileText,
  IconFilter,
  IconFilterFilled,
  IconFlag,
  IconFlagFilled,
  IconFolder,
  IconFolderFilled,
  IconForms,
  IconGauge,
  IconGridDots,
  IconGuitarPick,
  IconGuitarPickFilled,
  IconHash,
  IconHeading,
  IconHeadphones,
  IconHeadphonesFilled,
  IconHeart,
  IconHeartFilled,
  IconHighlight,
  IconHome,
  IconHomeFilled,
  IconInfoCircle,
  IconInfoCircleFilled,
  IconItalic,
  IconKeyboard,
  IconLayersIntersect,
  IconLayoutColumns,
  IconLayoutDashboard,
  IconLayoutDashboardFilled,
  IconLayoutGrid,
  IconLayoutGridFilled,
  IconLayoutNavbar,
  IconLayoutNavbarExpand,
  IconLetterCase,
  IconLifebuoy,
  IconLink,
  IconList,
  IconListDetails,
  IconListNumbers,
  IconLoader2,
  IconLock,
  IconLockFilled,
  IconMail,
  IconMailFilled,
  IconMap,
  IconMapPin,
  IconMapPinFilled,
  IconMarkdown,
  IconMenu2,
  IconMessage,
  IconMessage2,
  IconMessageCircle,
  IconMessageCircleFilled,
  IconMessageFilled,
  IconMicrophone,
  IconMicrophoneFilled,
  IconMinus,
  IconMoodSmile,
  IconMoodSmileFilled,
  IconMoon,
  IconMoonFilled,
  IconMusic,
  IconPackage,
  IconPalette,
  IconPencil,
  IconPhone,
  IconPhoneFilled,
  IconPhoto,
  IconPhotoFilled,
  IconPhotoOff,
  IconPiano,
  IconPin,
  IconPinFilled,
  IconPlayerPause,
  IconPlayerPauseFilled,
  IconPlayerPlay,
  IconPlayerPlayFilled,
  IconPlayerRecord,
  IconPlayerRecordFilled,
  IconPlayerStop,
  IconPlayerStopFilled,
  IconPlus,
  IconPointFilled,
  IconProgress,
  IconQrcode,
  IconQuestionMark,
  IconQuote,
  IconRefresh,
  IconRepeat,
  IconRocket,
  IconRotate,
  IconSearch,
  IconSelector,
  IconSeparator,
  IconSettings,
  IconSettingsFilled,
  IconShare,
  IconShield,
  IconShieldCheck,
  IconShieldCheckFilled,
  IconShieldFilled,
  IconShoppingCart,
  IconSparkles,
  IconSquareRounded,
  IconStack2,
  IconStar,
  IconStarFilled,
  IconStrikethrough,
  IconSun,
  IconSunFilled,
  IconTable,
  IconTag,
  IconTarget,
  IconTimeline,
  IconToggleLeft,
  IconToggleRight,
  IconToggleRightFilled,
  IconTrash,
  IconTrashFilled,
  IconTypography,
  IconUnderline,
  IconUpload,
  IconUser,
  IconUserCircle,
  IconUserFilled,
  IconVolume,
  IconVolumeOff,
  IconWaveSine,
  IconWebhook,
  IconWifi,
  IconWorld,
  IconWorldWww,
  IconX,
} from '@tabler/icons-react-native';
import type { IconRegistry } from '../types';

export const tablerIcons: IconRegistry = {
  accordion: { outlined: IconLayoutNavbarExpand },
  'alert-circle': { outlined: IconAlertCircle, filled: IconAlertCircleFilled },
  alignCenter: { outlined: IconAlignCenter },
  alignJustify: { outlined: IconAlignJustified },
  alignLeft: { outlined: IconAlignLeft },
  alignRight: { outlined: IconAlignRight },
  'arrow-down': { outlined: IconArrowDown },
  'arrow-left': { outlined: IconArrowLeft },
  'arrow-right': { outlined: IconArrowRight },
  'arrow-up': { outlined: IconArrowUp },
  arrowDown: { outlined: IconArrowDown },
  arrowLeft: { outlined: IconArrowLeft },
  arrowRight: { outlined: IconArrowRight },
  arrowUp: { outlined: IconArrowUp },
  autocomplete: { outlined: IconSearch },
  avatar: { outlined: IconUserCircle },
  badge: { outlined: IconBadge },
  'bar-chart': { outlined: IconChartBar },
  bell: { outlined: IconBell, filled: IconBellFilled },
  block: { outlined: IconBan },
  bluetooth: { outlined: IconBluetooth },
  bold: { outlined: IconBold },
  bolt: { outlined: IconBolt, filled: IconBoltFilled },
  bone: { outlined: IconBone },
  bookmark: { outlined: IconBookmark, filled: IconBookmarkFilled },
  breadcrumbs: { outlined: IconChevronRight },
  button: { outlined: IconSquareRounded },
  calendar: { outlined: IconCalendar, filled: IconCalendarFilled },
  camera: { outlined: IconCamera, filled: IconCameraFilled },
  card: { outlined: IconCards },
  carousel: { outlined: IconCarouselHorizontal },
  cart: { outlined: IconShoppingCart },
  'chart-area': { outlined: IconChartArea },
  'chart-bar': { outlined: IconChartBar },
  'chart-donut': { outlined: IconChartDonut },
  'chart-heatmap': { outlined: IconGridDots },
  'chart-line': { outlined: IconChartLine },
  'chart-pie': { outlined: IconChartPie },
  'chart-scatter': { outlined: IconChartDots },
  'chart-sparkline': { outlined: IconChartLine },
  chat: { outlined: IconMessage, filled: IconMessageFilled },
  check: { outlined: IconCheck },
  'chevron-down': { outlined: IconChevronDown },
  'chevron-left': { outlined: IconChevronLeft },
  'chevron-right': { outlined: IconChevronRight },
  'chevron-up': { outlined: IconChevronUp },
  chevronDown: { outlined: IconChevronDown },
  chevronLeft: { outlined: IconChevronLeft },
  chevronRight: { outlined: IconChevronRight },
  chevronUp: { outlined: IconChevronUp },
  'chevrons-up-down': { outlined: IconSelector },
  chip: { outlined: IconTag },
  circle: { outlined: IconCircle, filled: IconCircleFilled },
  clock: { outlined: IconClock, filled: IconClockFilled },
  close: { outlined: IconX },
  code: { outlined: IconCode },
  cog: { outlined: IconSettings, filled: IconSettingsFilled },
  color: { outlined: IconColorSwatch },
  colors: { outlined: IconPalette },
  compress: { outlined: IconArrowsMinimize },
  container: { outlined: IconBox },
  contrast: { outlined: IconContrast, filled: IconContrastFilled },
  copy: { outlined: IconCopy },
  database: { outlined: IconDatabase },
  datatable: { outlined: IconTable },
  delete: { outlined: IconTrash, filled: IconTrashFilled },
  dialog: { outlined: IconAppWindow },
  divider: { outlined: IconSeparator },
  dots: { outlined: IconDots },
  download: { outlined: IconDownload },
  edit: { outlined: IconPencil },
  ellipsis: { outlined: IconDots },
  'ellipsis-h': { outlined: IconDots },
  email: { outlined: IconMail, filled: IconMailFilled },
  emoji: { outlined: IconMoodSmile, filled: IconMoodSmileFilled },
  error: { outlined: IconAlertCircle, filled: IconAlertCircleFilled },
  exclamation: { outlined: IconExclamationCircle, filled: IconExclamationCircleFilled },
  expand: { outlined: IconArrowsMaximize },
  'external-link': { outlined: IconExternalLink },
  eye: { outlined: IconEye, filled: IconEyeFilled },
  eyeOff: { outlined: IconEyeOff },
  file: { outlined: IconFile, filled: IconFileFilled },
  filter: { outlined: IconFilter, filled: IconFilterFilled },
  flag: { outlined: IconFlag, filled: IconFlagFilled },
  flex: { outlined: IconLayoutColumns },
  folder: { outlined: IconFolder, filled: IconFolderFilled },
  font: { outlined: IconTypography },
  form: { outlined: IconForms },
  funnel: { outlined: IconFilter, filled: IconFilterFilled },
  gallery: { outlined: IconPhoto, filled: IconPhotoFilled },
  globe: { outlined: IconWorld },
  grid: { outlined: IconLayoutGrid, filled: IconLayoutGridFilled },
  guitar: { outlined: IconGuitarPick, filled: IconGuitarPickFilled },
  heading: { outlined: IconHeading },
  headphones: { outlined: IconHeadphones, filled: IconHeadphonesFilled },
  heart: { outlined: IconHeart, filled: IconHeartFilled },
  highlight: { outlined: IconHighlight },
  home: { outlined: IconHome, filled: IconHomeFilled },
  hook: { outlined: IconWebhook },
  image: { outlined: IconPhoto, filled: IconPhotoFilled },
  'image-off': { outlined: IconPhotoOff },
  indicator: { outlined: IconPointFilled },
  info: { outlined: IconInfoCircle, filled: IconInfoCircleFilled },
  input: { outlined: IconCursorText },
  italic: { outlined: IconItalic },
  keyboard: { outlined: IconKeyboard },
  keycap: { outlined: IconKeyboard },
  knob: { outlined: IconAdjustments, filled: IconAdjustmentsFilled },
  knobs: { outlined: IconAdjustments, filled: IconAdjustmentsFilled },
  'layer-mask': { outlined: IconLayersIntersect },
  layers: { outlined: IconStack2 },
  'line-chart': { outlined: IconChartLine },
  linechart: { outlined: IconChartLine },
  link: { outlined: IconLink },
  list: { outlined: IconList },
  listOrdered: { outlined: IconListNumbers },
  listUnordered: { outlined: IconList },
  loader: { outlined: IconLoader2 },
  loading: { outlined: IconLoader2 },
  location: { outlined: IconMapPin, filled: IconMapPinFilled },
  lock: { outlined: IconLock, filled: IconLockFilled },
  mail: { outlined: IconMail, filled: IconMailFilled },
  map: { outlined: IconMap },
  markdown: { outlined: IconMarkdown },
  masonry: { outlined: IconLayoutDashboard, filled: IconLayoutDashboardFilled },
  menu: { outlined: IconMenu2 },
  message: { outlined: IconMessageCircle, filled: IconMessageCircleFilled },
  'message-circle': { outlined: IconMessageCircle, filled: IconMessageCircleFilled },
  mic: { outlined: IconMicrophone, filled: IconMicrophoneFilled },
  microphone: { outlined: IconMicrophone, filled: IconMicrophoneFilled },
  minus: { outlined: IconMinus },
  moon: { outlined: IconMoon, filled: IconMoonFilled },
  music: { outlined: IconMusic },
  number: { outlined: IconHash },
  package: { outlined: IconPackage },
  paddingFrame: { outlined: IconBorderOuter },
  pagination: { outlined: IconDots },
  palette: { outlined: IconPalette },
  paper: { outlined: IconFileDescription },
  pause: { outlined: IconPlayerPause, filled: IconPlayerPauseFilled },
  person: { outlined: IconUser, filled: IconUserFilled },
  phone: { outlined: IconPhone, filled: IconPhoneFilled },
  piano: { outlined: IconPiano },
  pin: { outlined: IconPin, filled: IconPinFilled },
  plan: { outlined: IconClipboardList },
  play: { outlined: IconPlayerPlay, filled: IconPlayerPlayFilled },
  plus: { outlined: IconPlus },
  popover: { outlined: IconMessage2 },
  progress: { outlined: IconProgress },
  'progress-shield': { outlined: IconShieldCheck, filled: IconShieldCheckFilled },
  qrcode: { outlined: IconQrcode },
  question: { outlined: IconQuestionMark },
  quote: { outlined: IconQuote },
  radio: { outlined: IconCircleDot },
  rating: { outlined: IconStar, filled: IconStarFilled },
  record: { outlined: IconPlayerRecord, filled: IconPlayerRecordFilled },
  redo: { outlined: IconArrowForwardUp },
  refresh: { outlined: IconRefresh },
  repeat: { outlined: IconRepeat },
  richtext: { outlined: IconFileText },
  rocket: { outlined: IconRocket },
  rotate: { outlined: IconRotate },
  save: { outlined: IconDeviceFloppy },
  search: { outlined: IconSearch },
  select: { outlined: IconSelector },
  'selector-vertical': { outlined: IconSelector },
  selectorVertical: { outlined: IconSelector },
  settings: { outlined: IconSettings, filled: IconSettingsFilled },
  share: { outlined: IconShare },
  sheild: { outlined: IconShield, filled: IconShieldFilled },
  shop: { outlined: IconBuildingStore },
  slider: { outlined: IconAdjustmentsHorizontal },
  smile: { outlined: IconMoodSmile, filled: IconMoodSmileFilled },
  sort: { outlined: IconArrowsSort },
  sparkles: { outlined: IconSparkles },
  speedometer: { outlined: IconGauge },
  splitTrack: { outlined: IconColumns },
  spoiler: { outlined: IconEyeOff },
  spotlight: { outlined: IconSearch },
  star: { outlined: IconStar, filled: IconStarFilled },
  stepper: { outlined: IconListNumbers },
  stop: { outlined: IconPlayerStop, filled: IconPlayerStopFilled },
  strikethrough: { outlined: IconStrikethrough },
  success: { outlined: IconCircleCheck, filled: IconCircleCheckFilled },
  sun: { outlined: IconSun, filled: IconSunFilled },
  support: { outlined: IconLifebuoy },
  switch: { outlined: IconToggleRight, filled: IconToggleRightFilled },
  table: { outlined: IconTable },
  tableofcontents: { outlined: IconListDetails },
  tabs: { outlined: IconLayoutNavbar },
  target: { outlined: IconTarget },
  text: { outlined: IconLetterCase },
  textarea: { outlined: IconForms },
  timeline: { outlined: IconTimeline },
  times: { outlined: IconX },
  title: { outlined: IconHeading },
  toast: { outlined: IconMessage2 },
  toggle: { outlined: IconToggleRight, filled: IconToggleRightFilled },
  toggleOff: { outlined: IconToggleLeft },
  toggleOn: { outlined: IconToggleRight, filled: IconToggleRightFilled },
  tooltip: { outlined: IconMessage2 },
  trash: { outlined: IconTrash, filled: IconTrashFilled },
  tree: { outlined: IconBinaryTree },
  tuning: { outlined: IconAdjustments, filled: IconAdjustmentsFilled },
  underline: { outlined: IconUnderline },
  undo: { outlined: IconArrowBackUp },
  upload: { outlined: IconUpload },
  user: { outlined: IconUser, filled: IconUserFilled },
  'volume-off': { outlined: IconVolumeOff },
  'volume-up': { outlined: IconVolume },
  volumeOff: { outlined: IconVolumeOff },
  warning: { outlined: IconAlertTriangle, filled: IconAlertTriangleFilled },
  waveform: { outlined: IconWaveSine },
  web: { outlined: IconWorldWww },
  wifi: { outlined: IconWifi },
  x: { outlined: IconX },
};
